<template>
  <TopBarForDrawer>
    <template #summaries>
      <InsightDatasetCountContainer
        v-if="!breadcrumbsOnly"
        :is-loading="isLoading || filtersLoading"
      />
      <DatePickerRangeDisplay
        v-if="filterConfig.datepicker && !hideDatePicker"
        :is-loading="filtersLoading || disabledFilters"
      />
    </template>

    <template #page-title>
      <TopBarForDrawerTitle
        v-if="!breadcrumbsOnly"
        :is-loading="isLoading"
        :page-title="route.meta.title"
        :page-description="description"
      />
    </template>

    <template #topbar-actions>
      <section
        v-if="pageHasFilters[route.name] && !breadcrumbsOnly"
        class="insights-actions"
        data-png-remove
        aria-label="Access to Filters"
      >
        <FiltersDrawerToggle
          v-model="openDrawer"
          :use-wide-screen-breakpoint="false"
          :is-loading="isLoading"
          :are-filters-applied="areNonIgnorableFiltersApplied"
        />
        <InsightsTopBarActions
          :is-loading="filtersLoading"
          :hide-filters="hideFilters"
          :page-is-shareable="pageIsShareable[route.name]"
          :disabled-filters="disabledFilters"
          :hide-date-picker="hideDatePicker"
          :filter-config="filterConfig"
        />
      </section>
    </template>
  </TopBarForDrawer>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import TopBarForDrawer from '@/components/shared/topbar/TopBarForDrawer.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import InsightsTopBarActions from '@/components/insights/InsightsTopBarActions.vue';
import FiltersDrawerToggle from '@/components/filter/FiltersDrawerToggle.vue';
import InsightDatasetCountContainer from '@/components/insights/InsightDatasetCountContainer.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppBase } from '@/stores/app/base';
import { useAppAuthData } from '@/stores/app/authData';
import { useFilters } from '@/stores/filters/filters';
import DatePickerRangeDisplay from '@/components/filter/dateFilters/DatePicker/DatePickerRangeDisplay.vue';
import { pageDescriptions } from '@/helpers/constants/pageDescriptions';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:model-value']);

// Route
const route = useRoute();

// Stores
const appLoadingStore = useAppLoading();
const { pageLoading, halfTopBarLoading, filtersLoading } = storeToRefs(appLoadingStore);

const appBaseStore = useAppBase();
const {
  pageHasFilters,
  pageIsShareable,
  pageDisabledFilters,
  pageHideDatePicker,
  pageHidesFilterChips,
} = storeToRefs(appBaseStore);
const { isDemographicSurveyReportRestricted } = storeToRefs(useAppAuthData());

const filterStore = useFilters();
const { filterConfig, areNonIgnorableFiltersApplied } = storeToRefs(filterStore);

// Computed
const isLoading = computed(() => pageLoading.value && halfTopBarLoading.value);

const breadcrumbsOnly = computed(() => route.name === 'demographic-survey-report'
  && isDemographicSurveyReportRestricted.value);

const openDrawer = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:model-value', value),
});

const disabledFilters = computed(() => pageDisabledFilters.value[route.name] || false);

const hideDatePicker = computed(() => pageHideDatePicker.value[route.name] || false);

const hideFilters = computed(() => pageHidesFilterChips.value[route.name] || false);

const description = computed(() => pageDescriptions()[route.name] || '');
</script>

<style lang="less" scoped>
.insights-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
